import React from 'react';
import { TDay } from '../../../../../../types/e-visits';
import { IAddConfig, ICalendarEvent, IRenderInfo, TEventType } from '../../../calendars';
import { TPositionedEvent } from '../../week-utils';
import { WeekDayItemSC } from './week-day.styled';

interface WeekDayItemProps<T> extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'> {
  item: TPositionedEvent<ICalendarEvent<T>>;
  onClick: (
    nodeRef: React.RefObject<HTMLDivElement>,
    event: ICalendarEvent,
    eventType: TEventType,
    index: number,
  ) => void;
  renderEvent: (info: IRenderInfo<T>) => React.ReactElement;
  weekDays: TDay[];
  timeLineWidth: number;
  index: number;
  onEdit: (nodeRef: React.RefObject<HTMLDivElement>, config: IAddConfig<T>) => void;
}

const WeekDayItem = <T extends any>({
  renderEvent,
  item,
  onClick,
  weekDays,
  timeLineWidth,
  index,
  onEdit,
  ...rest
}: WeekDayItemProps<T>) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const { event, position } = item;

  const date = new Date(event.start);
  const weekDay = date.getDay();
  const column = weekDays.findIndex((wd) => wd === weekDay);

  const renderInfo = React.useMemo<IRenderInfo<T>>(
    () => ({
      index,
      item: event,
      onEdit: (config: IAddConfig<T>) => onEdit(ref, { ...config, index }),
      onClick: () => onClick(ref, event, 'event', index),
    }),
    [index, event, onEdit, onClick],
  );

  return (
    <WeekDayItemSC
      role="button"
      aria-hidden="true"
      ref={ref}
      zIndex={position.zIndex + 999}
      maxZIndex={position.maxZIndex + 999}
      style={{
        position: 'absolute',
        transform: `translate(${column * timeLineWidth + position.left * timeLineWidth}px, ${
          position.top
        }px)`,
        height: position.height,
        width: position.width * timeLineWidth * 0.9,
        border: '1px solid transparent',
      }}
      {...rest}
    >
      {renderEvent(renderInfo)}
    </WeekDayItemSC>
  );
};

export default React.memo(WeekDayItem) as typeof WeekDayItem;
